import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Eye, Edit, PlusCircle, TrendingUp, Settings, CheckCircle, AlertCircle } from 'lucide-react';
import AdminNavbar from '../components/admin/AdminNavbar';
import SEOHead from '../components/SEOHead';
import { useAuth } from '../contexts/AuthContext';
import { supabase, BlogPost } from '../utils/supabase';

const AdminDashboardPage = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [stats, setStats] = useState({
    totalPosts: 0,
    publishedPosts: 0,
    draftPosts: 0,
    totalViews: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [postsWithoutLinkedIn, setPostsWithoutLinkedIn] = useState(0);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPosts();
    }
  }, [isAuthenticated]);

  const fetchPosts = async () => {
    try {
      const { data: allPosts, error: allError } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (allError) throw allError;

      const recentPosts = (allPosts || []).slice(0, 5);
      setPosts(recentPosts);

      const published = (allPosts || []).filter(p => p.status === 'published').length;
      const draft = (allPosts || []).filter(p => p.status === 'draft').length;
      const missingLinkedIn = (allPosts || []).filter(p => !p.linkedin_url || p.linkedin_url.trim() === '').length;

      setStats({
        totalPosts: (allPosts || []).length,
        publishedPosts: published,
        draftPosts: draft,
        totalViews: 0
      });
      setPostsWithoutLinkedIn(missingLinkedIn);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (loading || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ctc-blue"></div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Dashboard - Blog Admin"
        description="Admin dashboard for managing blog posts"
        noIndex={true}
      />
      <AdminNavbar />

      <div className="pt-24 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Dashboard</h1>
            <p className="text-gray-600">Welcome back! Here's an overview of your blog.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              onClick={() => navigate('/admin/posts?status=all')}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 cursor-pointer hover:shadow-md hover:border-blue-200 transition-all"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="text-blue-600" size={24} />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">{stats.totalPosts}</h3>
              <p className="text-gray-600 text-sm">Total Posts</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              onClick={() => navigate('/admin/posts?status=published')}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 cursor-pointer hover:shadow-md hover:border-green-200 transition-all"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Eye className="text-green-600" size={24} />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">{stats.publishedPosts}</h3>
              <p className="text-gray-600 text-sm">Published</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              onClick={() => navigate('/admin/posts?status=draft')}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 cursor-pointer hover:shadow-md hover:border-yellow-200 transition-all"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Edit className="text-yellow-600" size={24} />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">{stats.draftPosts}</h3>
              <p className="text-gray-600 text-sm">Drafts</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="text-purple-600" size={24} />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">
                {stats.totalViews > 0 ? stats.totalViews.toLocaleString() : 'N/A'}
              </h3>
              <p className="text-gray-600 text-sm">Total Views</p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            className="mb-8"
          >
            <Link
              to="/admin/banner-settings"
              className="bg-gradient-to-r from-ctc-blue to-ctc-blue-dark text-white rounded-xl shadow-sm p-6 border border-blue-200 hover:shadow-md transition-all flex items-center justify-between group"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Settings className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Seasonal Banner Settings</h3>
                  <p className="text-blue-100 text-sm">Manage homepage seasonal support banner</p>
                </div>
              </div>
              <div className="text-white group-hover:translate-x-1 transition-transform">
                →
              </div>
            </Link>
          </motion.div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <h2 className="text-2xl font-bold text-gray-900">Recent Posts</h2>
                {postsWithoutLinkedIn > 0 && (
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium flex items-center space-x-1">
                    <AlertCircle size={14} />
                    <span>{postsWithoutLinkedIn} need LinkedIn URL</span>
                  </span>
                )}
              </div>
              <Link
                to="/admin/posts/new"
                className="bg-ctc-blue text-white px-4 py-2 rounded-lg font-semibold hover:bg-ctc-blue-dark transition-colors flex items-center space-x-2"
              >
                <PlusCircle size={20} />
                <span>New Post</span>
              </Link>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-ctc-blue"></div>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="mx-auto text-gray-300 mb-4" size={48} />
                <p className="text-gray-500 mb-4">No posts yet</p>
                <Link
                  to="/admin/posts/new"
                  className="inline-flex items-center space-x-2 text-ctc-blue hover:text-ctc-blue-dark transition-colors"
                >
                  <PlusCircle size={20} />
                  <span>Create your first post</span>
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Title</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">LinkedIn</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map((post) => (
                      <tr key={post.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4">
                          <div className="font-medium text-gray-900">{post.title}</div>
                          <div className="text-sm text-gray-500">{post.author}</div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                            post.status === 'published'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {post.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <Link
                            to={`/admin/posts/${post.id}?focus=linkedin`}
                            className="group relative inline-flex items-center"
                            title={post.linkedin_url ? 'LinkedIn URL added' : 'Add LinkedIn URL'}
                          >
                            {post.linkedin_url ? (
                              <CheckCircle size={18} className="text-green-600 group-hover:scale-110 transition-transform cursor-pointer" />
                            ) : (
                              <AlertCircle size={18} className="text-orange-500 group-hover:scale-110 transition-transform cursor-pointer" />
                            )}
                          </Link>
                        </td>
                        <td className="py-3 px-4 text-gray-600">{formatDate(post.date)}</td>
                        <td className="py-3 px-4">
                          <Link
                            to={`/admin/posts/${post.id}`}
                            className="text-ctc-blue hover:text-ctc-blue-dark transition-colors font-medium"
                          >
                            Edit
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {posts.length > 0 && (
              <div className="mt-6 text-center">
                <Link
                  to="/admin/posts"
                  className="text-ctc-blue hover:text-ctc-blue-dark transition-colors font-medium"
                >
                  View all posts →
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
